import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Reveal, Overline } from "@/components/site/Reveal";
import { WorldMap } from "@/components/site/WorldMap";
import { useLanguage } from "@/context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => Number(n).toLocaleString("en-US");

// Fallback so the section works even before/without the backend.
const FALLBACK = {
  worldwideDisplay: "572,000+",
  spellings: [
    { s: "McDonald", n: 340769 },
    { s: "MacDonald", n: 231721 },
  ],
  countries: [
    { c: "United States", n: 234498 },
    { c: "Australia", n: 48189 },
    { c: "Canada", n: 37703 },
    { c: "England", n: 31859 },
    { c: "Scotland", n: 17506 },
    { c: "Jamaica", n: 9385 },
    { c: "Ireland", n: 6433 },
    { c: "South Africa", n: 5817 },
    { c: "New Zealand", n: 5539 },
    { c: "Northern Ireland", n: 3533 },
  ],
  source: "Forebears (McDonald & MacDonald) and National Records of Scotland.",
  updatedAt: "2026-06",
};

function Stat({ value, label, testid }) {
  return (
    <div data-testid={testid} className="rounded-lg border border-clan-ink/10 bg-clan-surface p-6">
      <p className="font-serif text-4xl text-clan-accent lg:text-5xl">{value}</p>
      <p className="mt-3 text-sm leading-relaxed text-clan-muted">{label}</p>
    </div>
  );
}

export function Distribution() {
  const { t } = useLanguage();
  const d = t.distribution;
  const [data, setData] = useState(FALLBACK);

  useEffect(() => {
    let alive = true;
    axios
      .get(`${API}/distribution`)
      .then((res) => {
        if (alive && res.data && Array.isArray(res.data.countries)) setData(res.data);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  const spellingMax = Math.max(...data.spellings.map((x) => x.n), 1);
  const topCountries = [...data.countries].sort((a, b) => b.n - a.n).slice(0, 6);

  return (
    <section id="distribution" data-testid="distribution-section" className="relative bg-clan-bg py-28 lg:py-40">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="max-w-3xl">
          <Reveal>
            <Overline>{d.overline}</Overline>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-6 font-serif text-4xl font-light leading-[1.05] tracking-tight text-clan-ink sm:text-5xl lg:text-6xl pearl">
              {d.title}
            </h2>
          </Reveal>
          <Reveal delay={0.18}>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-clan-muted">{d.intro}</p>
          </Reveal>
        </div>

        {/* Headline number */}
        <Reveal delay={0.1}>
          <div className="mt-14 rounded-lg border border-clan-accent/25 bg-clan-accent/5 p-8 lg:p-12">
            <p data-testid="dist-worldwide" className="font-serif text-6xl font-medium leading-none text-clan-accent sm:text-7xl lg:text-8xl">
              {data.worldwideDisplay || "572,000+"}
            </p>
            <p className="mt-4 font-mono text-xs uppercase tracking-[0.25em] text-clan-ink">{d.worldwideLabel}</p>
            <p className="mt-2 max-w-xl text-sm text-clan-muted">{d.worldwideNote}</p>
          </div>
        </Reveal>

        {/* Stat tiles */}
        <div className="mt-6 grid gap-5 md:grid-cols-3">
          <Reveal delay={0.12}><Stat testid="dist-stat-scotland" value="No. 2" label={d.statScotland} /></Reveal>
          <Reveal delay={0.18}><Stat testid="dist-stat-us" value="234,000+" label={d.statUS} /></Reveal>
          <Reveal delay={0.24}><Stat testid="dist-stat-ireland" value="10,000+" label={d.statIreland} /></Reveal>
        </div>

        {/* Interactive world map */}
        <Reveal delay={0.14} y={30}>
          <div className="mt-8 overflow-hidden rounded-lg border border-clan-ink/10 bg-clan-surface p-6 lg:p-8">
            <p className="mb-5 font-mono text-xs uppercase tracking-[0.22em] text-clan-accent">{d.countriesTitle}</p>
            <WorldMap countries={data.countries} />
            <div className="mt-6 flex flex-wrap gap-2.5 border-t border-clan-ink/10 pt-6">
              {topCountries.map((x) => (
                <span
                  key={x.c}
                  data-testid={`dist-chip-${x.c.replace(/\s+/g, "-").toLowerCase()}`}
                  className="flex items-center gap-2 rounded-md border border-clan-ink/10 bg-clan-bg/50 px-3.5 py-1.5"
                >
                  <span className="h-2 w-2 rounded-full bg-clan-accent" />
                  <span className="text-sm text-clan-ink">{x.c}</span>
                  <span className="font-mono text-xs text-clan-muted">{fmt(x.n)}</span>
                </span>
              ))}
            </div>
          </div>
        </Reveal>

        {/* By spelling */}
        <Reveal delay={0.2} y={30}>
          <div className="mt-6 rounded-lg border border-clan-ink/10 bg-clan-surface p-7 lg:p-8">
            <p className="mb-6 font-mono text-xs uppercase tracking-[0.22em] text-clan-accent">{d.spellingTitle}</p>
            <div className="grid gap-6 md:grid-cols-2">
              {data.spellings.map((x) => (
                <div key={x.s}>
                  <div className="mb-2 flex items-baseline justify-between">
                    <span className="font-serif text-xl text-clan-ink">{x.s}</span>
                    <span className="font-mono text-sm text-clan-accent">{fmt(x.n)}</span>
                  </div>
                  <div className="h-3 overflow-hidden rounded-md bg-clan-ink/[0.06]">
                    <motion.div
                      initial={{ width: 0 }}
                      whileInView={{ width: `${(x.n / spellingMax) * 100}%` }}
                      viewport={{ once: true, margin: "-40px" }}
                      transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
                      className="h-full rounded-md bg-clan-accent"
                    />
                  </div>
                </div>
              ))}
            </div>
            <p className="mt-6 text-sm leading-relaxed text-clan-muted">
              McDonnell, MacDonnell &amp; McConnell — the Irish and further branches — add many thousands more.
            </p>
          </div>
        </Reveal>

        <p className="mt-8 text-xs text-clan-muted/60">
          {d.source} {data.updatedAt ? `· ${data.updatedAt}` : ""}
        </p>
      </div>
    </section>
  );
}
